import React, { createContext, useContext, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import {addCountries} from "../../../reducer";

// Créer le contexte
const CurrencyContext = createContext(null);

// API endpoints (à adapter selon votre configuration)
const apis = {
    searchCountry: (query) => `/flags/search/country/${query}`,
};

// Provider du contexte
export const CurrencyProvider = ({ children }) => {
    const dispatch = useDispatch();
    const countries = useSelector(state => state.global.countries);

    /**
     * Récupère le symbole de devise par code (EUR, USD, MGA, etc.)
     * @param {string} currencyCode - Le code de la devise (ex: "EUR", "USD")
     * @returns {Promise<string>} Le symbole de la devise (ex: "€", "$")
     */
    const getCurrencySymbolByCode = useCallback(async (currencyCode) => {
        if (!currencyCode) return null;

        // Chercher dans les pays déjà chargés
        const country = countries?.find(
            (c) => c.currencies?.code?.toLowerCase() === currencyCode.toLowerCase()
        );

        if (country) {
            return country.currencies?.symbol;
        }

        // Si pas trouvé, faire un appel API pour récupérer les infos
        try {
            const { data } = await axios.get(apis.searchCountry(currencyCode));
            if (data) {
                // Ajouter le pays à la liste (action Redux à adapter selon votre store)
                await dispatch(addCountries([data, ...countries]));
                return data?.currencies?.symbol;
            }
        } catch (error) {
            console.error("Error fetching currency by code:", error);
        }

        return null;
    }, [countries, dispatch]);

    /**
     * Récupère le symbole de devise par adresse complète
     * @param {string} address - L'adresse complète (ex: "123 rue, Paris, France")
     * @returns {Promise<string>} Le symbole de la devise
     */
    const getCurrencySymbolByAddress = useCallback(async (address) => {
        if (!address) return null;

        // Parser l'adresse pour extraire le pays
        const addressParts = address?.split(', ');
        const countryName = addressParts[addressParts?.length - 1];

        if (!countryName) return null;

        // Chercher le pays dans la liste existante
        let country = countries?.find(tmp =>
            countryName.toLowerCase() === tmp.name?.toLowerCase() ||
            countryName.toLowerCase() === tmp.nativeName?.toLowerCase()
        );

        if (!country) {
            // Si pas trouvé, faire un appel API
            try {
                const { data } = await axios.get(apis.searchCountry(countryName));
                if (data) {
                    await dispatch(addCountries([data, ...countries]));
                    return data?.currencies?.symbol;
                }
            } catch (error) {
                console.error("Error fetching currency by address:", error);
            }
            return null;
        }

        return country?.currencies?.symbol;
    }, [countries, dispatch]);

    /**
     * Récupère les infos complètes de la devise (symbole, code, nom)
     * @param {string} codeOrAddress - Code de devise ou adresse
     * @param {boolean} isAddress - Si true, traite comme une adresse
     * @returns {Promise<object>} Objet contenant {symbol, code, name}
     */
    const getCurrencyInfo = useCallback(async (codeOrAddress, isAddress = false) => {
        if (!codeOrAddress) return null;

        let country;

        if (isAddress) {
            const addressParts = codeOrAddress?.split(', ');
            const countryName = addressParts[addressParts?.length - 1];

            country = countries?.find(tmp =>
                countryName?.toLowerCase() === tmp.name?.toLowerCase() ||
                countryName?.toLowerCase() === tmp.nativeName?.toLowerCase()
            );

            if (!country) {
                try {
                    const { data } = await axios.get(apis.searchCountry(countryName));
                    if (data) {
                        await dispatch(addCountries([data, ...countries]));
                        country = data;
                    }
                } catch (error) {
                    console.error("Error fetching currency info:", error);
                }
            }
        } else {
            // Traiter comme un code de devise
            country = countries?.find(
                (c) => c.currencies?.code?.toLowerCase() === codeOrAddress.toLowerCase()
            );

            if (!country) {
                try {
                    const { data } = await axios.get(apis.searchCountry(codeOrAddress));
                    if (data) {
                        await dispatch(addCountries([data, ...countries]));
                        country = data;
                    }
                } catch (error) {
                    console.error("Error fetching currency info:", error);
                }
            }
        }

        if (country?.currencies) {
            return {
                symbol: country.currencies.symbol,
                code: country.currencies.code,
                name: country.currencies.name
            };
        }

        return null;
    }, [countries, dispatch]);

    /**
     * Récupère le symbole de devise avec fallback
     * @param {string} currencyCode - Code de devise
     * @param {string} fallback - Symbole par défaut (ex: "€")
     * @returns {Promise<string>}
     */
    const getCurrencySymbolWithFallback = useCallback(async (currencyCode, fallback = "€") => {
        const symbol = await getCurrencySymbolByCode(currencyCode);
        return symbol || fallback;
    }, [getCurrencySymbolByCode]);

    const value = {
        countries,
        getCurrencySymbolByCode,
        getCurrencySymbolByAddress,
        getCurrencyInfo,
        getCurrencySymbolWithFallback
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};

// Hook personnalisé pour utiliser le contexte
export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error("useCurrency must be used within a CurrencyProvider");
    }
    return context;
};

export default CurrencyContext;
